import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type EmojiEntry,
  type EmojiGroup,
  loadEmojiGroups,
  recentEmoji,
  rememberEmoji,
  savedTone,
  saveTone,
  searchEmoji,
  withTone,
} from "./emoji";
import { SearchIcon, XIcon } from "./icons";
import { cn, focusRing } from "./utils";

/** Emoji per row. Arrow keys move along the grid by this much. */
export const EMOJI_COLUMNS = 8;

const TONES = ["✋", "✋🏻", "✋🏼", "✋🏽", "✋🏾", "✋🏿"];
const TONE_NAMES = ["Default", "Light", "Medium light", "Medium", "Medium dark", "Dark"];

type Section = { key: string; label: string; icon: string; cells: { emoji: string; label: string }[] };

/** Height of a section's heading, in pixels. */
const HEADING = 28;
/** Rows drawn above and below what shows, so scrolling never reveals a gap. */
const BUFFER_ROWS = 4;
/** The grid's height before it can be measured, close to the picker's own. */
const FALLBACK_VIEW = 320;
/** The grid's width before it can be measured: the picker less its side padding. */
const FALLBACK_WIDTH = 336;

/** Where each section sits in the scrolled grid, worked out from the row height alone. */
function layoutOf(sections: Section[], rowHeight: number) {
  let top = 0;
  const tops: number[] = [];
  for (const s of sections) {
    tops.push(top);
    top += HEADING + Math.ceil(s.cells.length / EMOJI_COLUMNS) * rowHeight;
  }
  return { tops, total: top };
}

export type EmojiPickerProps = {
  /** Called with the emoji picked, already in the chosen skin tone. `keepOpen` is set for Shift and a pick. */
  onPick: (emoji: string, keepOpen: boolean) => void;
  /** Escape with nothing typed in the search. */
  onClose?: () => void;
  /** What the picker is for, such as "Add a reaction". */
  label?: string;
  autoFocus?: boolean;
  className?: string;
};

/**
 * The full set of emoji, with search, recently used, categories and skin tones. Arrow keys move
 * through the grid, Enter picks, and typing goes to the search. The data loads the first time a
 * picker opens.
 */
export function EmojiPicker({
  onPick,
  onClose,
  label = "Emoji",
  autoFocus = true,
  className,
}: EmojiPickerProps) {
  const [groups, setGroups] = useState<EmojiGroup[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(() => recentEmoji());
  const [tone, setTone] = useState(() => savedTone());
  const [choosingTone, setChoosingTone] = useState(false);
  const [active, setActive] = useState(0);
  const [preview, setPreview] = useState<{ emoji: string; label: string } | null>(null);
  const [shownKey, setShownKey] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  // Only the rows on screen, and a few either side, are drawn. About 1,900 emoji drawn at once made
  // the picker slow to open and every key press slow to answer.
  const [scrollTop, setScrollTop] = useState(0);
  const [view, setView] = useState({ width: FALLBACK_WIDTH, height: FALLBACK_VIEW });
  /** A cell the keyboard moved to, focused once it has been drawn. */
  const pendingFocus = useRef<number | null>(null);

  const load = useCallback(() => {
    setFailed(false);
    loadEmojiGroups()
      .then(setGroups)
      .catch(() => setFailed(true));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (autoFocus) search.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const labels = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups ?? []) {
      for (const e of g.emojis) {
        map.set(e.emoji, e.label);
        for (const [i, s] of (e.skins ?? []).entries())
          map.set(s, `${e.label}, ${TONE_NAMES[i + 1]?.toLowerCase()} skin tone`);
      }
    }
    return map;
  }, [groups]);

  const sections: Section[] = useMemo(() => {
    if (!groups) return [];
    const cell = (e: EmojiEntry) => ({ emoji: withTone(e, tone), label: e.label });
    if (query.trim()) {
      return [
        { key: "results", label: "Search results", icon: "", cells: searchEmoji(groups, query).map(cell) },
      ];
    }
    const list: Section[] = [];
    const known = recent.filter((e) => labels.has(e));
    if (known.length)
      list.push({
        key: "recent",
        label: "Recently used",
        icon: "🕘",
        cells: known.map((emoji) => ({ emoji, label: labels.get(emoji) ?? emoji })),
      });
    for (const g of groups)
      list.push({ key: g.key, label: g.label, icon: g.icon, cells: g.emojis.map(cell) });
    return list;
  }, [groups, query, recent, tone, labels]);

  // The grid as one list, so the keyboard can move through it. `starts` is where each section begins.
  const { flat, starts } = useMemo(() => {
    const all: { emoji: string; label: string }[] = [];
    const at: number[] = [];
    for (const s of sections) {
      at.push(all.length);
      all.push(...s.cells);
    }
    return { flat: all, starts: at };
  }, [sections]);

  const rowHeight = Math.max(32, view.width / EMOJI_COLUMNS);
  const layout = useMemo(() => layoutOf(sections, rowHeight), [sections, rowHeight]);

  // The grid's size, measured where the browser can, so rows match the cells' real height.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measure again once the grid shows
  useLayoutEffect(() => {
    const box = scroller.current;
    if (!box) return;
    const measure = () => {
      // Side padding is 8px each side.
      const width = box.clientWidth - 16;
      if (width > 0 && box.clientHeight > 0) setView({ width, height: box.clientHeight });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [groups]);

  const scrollTo = (top: number) => {
    const box = scroller.current;
    const max = Math.max(0, layout.total - view.height);
    const next = Math.max(0, Math.min(max, top));
    if (box) box.scrollTop = next;
    setScrollTop(next);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: back to the top whenever the search changes
  useEffect(() => {
    setActive(0);
    if (scroller.current) scroller.current.scrollTop = 0;
    setScrollTop(0);
  }, [query]);

  const pick = (emoji: string, keepOpen: boolean) => {
    setRecent(rememberEmoji(emoji));
    onPick(emoji, keepOpen);
  };

  /** The top and bottom of a cell's row in the scrolled grid. */
  const rowSpan = (index: number) => {
    const { s, row } = position(index);
    const top = (layout.tops[s] ?? 0) + HEADING + row * rowHeight;
    return { top, bottom: top + rowHeight };
  };

  const focusCell = (index: number) => {
    const next = Math.max(0, Math.min(flat.length - 1, index));
    setActive(next);
    // Scroll the row into view, under the sticky heading, then focus it once it is drawn.
    const { top, bottom } = rowSpan(next);
    if (top - HEADING < scrollTop) scrollTo(top - HEADING);
    else if (bottom > scrollTop + view.height) scrollTo(bottom - view.height);
    const drawn = grid.current?.querySelector<HTMLElement>(`[data-cell="${next}"]`);
    if (drawn) drawn.focus();
    else pendingFocus.current = next;
  };

  useLayoutEffect(() => {
    const wanted = pendingFocus.current;
    if (wanted === null) return;
    const el = grid.current?.querySelector<HTMLElement>(`[data-cell="${wanted}"]`);
    if (el) {
      pendingFocus.current = null;
      el.focus();
    }
  });

  /** Where a cell sits: its section, and its row and column inside it. */
  const position = (index: number) => {
    let s = 0;
    while (s + 1 < starts.length && (starts[s + 1] ?? 0) <= index) s++;
    const local = index - (starts[s] ?? 0);
    return { s, row: Math.floor(local / EMOJI_COLUMNS), col: local % EMOJI_COLUMNS };
  };
  const sectionLength = (s: number) => sections[s]?.cells.length ?? 0;
  const cellAt = (s: number, row: number, col: number) =>
    (starts[s] ?? 0) + Math.min(row * EMOJI_COLUMNS + col, sectionLength(s) - 1);

  const vertical = (index: number, down: boolean): number | null => {
    const { s, row, col } = position(index);
    const rows = Math.ceil(sectionLength(s) / EMOJI_COLUMNS);
    if (down) {
      if (row + 1 < rows) return cellAt(s, row + 1, col);
      return s + 1 < sections.length ? cellAt(s + 1, 0, col) : null;
    }
    if (row > 0) return cellAt(s, row - 1, col);
    if (s === 0) return -1;
    return cellAt(s - 1, Math.ceil(sectionLength(s - 1) / EMOJI_COLUMNS) - 1, col);
  };

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const index = Number((e.target as HTMLElement).dataset.cell ?? active);
    const go = (to: number | null) => {
      e.preventDefault();
      if (to === null) return;
      if (to < 0) search.current?.focus();
      else focusCell(to);
    };
    if (e.key === "ArrowRight") go(index + 1 < flat.length ? index + 1 : null);
    else if (e.key === "ArrowLeft") go(index > 0 ? index - 1 : null);
    else if (e.key === "ArrowDown") go(vertical(index, true));
    else if (e.key === "ArrowUp") go(vertical(index, false));
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(flat.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    } else if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Typing goes to the search.
      e.preventDefault();
      setQuery((q) => q + e.key);
      search.current?.focus();
    }
  };

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && flat.length) {
      e.preventDefault();
      focusCell(0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const first = flat[0];
      if (query.trim() && first) pick(first.emoji, e.shiftKey);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (query) setQuery("");
      else onClose?.();
    }
  };

  const jump = (key: string) => {
    setQuery("");
    setShownKey(key);
    const s = sections.findIndex((x) => x.key === key);
    if (s >= 0) requestAnimationFrame(() => scrollTo(layout.tops[s] ?? 0));
  };

  const onScroll = () => {
    const box = scroller.current;
    if (!box) return;
    setScrollTop(box.scrollTop);
    let current: string | null = null;
    sections.forEach((sec, i) => {
      if ((layout.tops[i] ?? 0) - 8 <= box.scrollTop) current = sec.key;
    });
    setShownKey(current);
  };

  // The rows to draw: those on screen and a few either side.
  const from = scrollTop - BUFFER_ROWS * rowHeight;
  const to = scrollTop + view.height + BUFFER_ROWS * rowHeight;
  const drawn = sections.flatMap((sec, si) => {
    const top = layout.tops[si] ?? 0;
    const rows = Math.ceil(sec.cells.length / EMOJI_COLUMNS);
    const height = HEADING + rows * rowHeight;
    if (top + height < from || top > to) return [];
    const first = Math.max(0, Math.floor((from - top - HEADING) / rowHeight));
    const last = Math.min(rows - 1, Math.ceil((to - top - HEADING) / rowHeight));
    return [{ sec, si, top, height, first, last }];
  });
  // The cell Tab reaches in the grid: the active one when drawn, otherwise the first drawn.
  const drawnFirst = drawn[0] ? (starts[drawn[0].si] ?? 0) + drawn[0].first * EMOJI_COLUMNS : 0;
  const drawnLast = (() => {
    const d = drawn[drawn.length - 1];
    if (!d) return -1;
    return (
      Math.min((starts[d.si] ?? 0) + (d.last + 1) * EMOJI_COLUMNS, (starts[d.si] ?? 0) + d.sec.cells.length) -
      1
    );
  })();
  const tabbable = active >= drawnFirst && active <= drawnLast ? active : drawnFirst;

  const shown = preview ?? flat[active] ?? null;
  const searching = query.trim().length > 0;

  return (
    <section
      aria-label={label}
      className={cn(
        "flex h-[420px] max-h-[min(420px,calc(100dvh-24px))] w-[352px] max-w-full flex-col overflow-hidden rounded-[16px] bg-linen shadow-menu",
        className,
      )}
    >
      <div className="flex-none px-3 pt-3">
        <label className="flex h-10 items-center gap-2 rounded-field border border-line bg-surface px-3 focus-within:border-blue">
          <SearchIcon size={16} className="flex-none text-ink-meta" />
          <input
            ref={search}
            type="search"
            aria-label="Search emoji"
            placeholder="Search emoji"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
            className="min-w-0 flex-1 border-0 bg-transparent font-sans text-[15px] text-ink outline-none placeholder:text-ink-meta [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                search.current?.focus();
              }}
              className={cn(
                "flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-ink-meta",
                focusRing,
              )}
            >
              <XIcon size={13} strokeWidth={2.6} />
            </button>
          )}
        </label>
      </div>
      {!searching && groups && (
        <nav aria-label="Emoji categories" className="flex flex-none justify-between px-2 pt-2">
          {sections.map((s) => (
            <button
              key={s.key}
              type="button"
              aria-label={s.label}
              aria-current={shownKey === s.key ? "true" : undefined}
              title={s.label}
              onClick={() => jump(s.key)}
              className={cn(
                "flex h-8 w-8 cursor-pointer items-center justify-center rounded-[8px] text-[18px] leading-none",
                shownKey === s.key || (!shownKey && s === sections[0])
                  ? "bg-blue-tint"
                  : "opacity-75 hover:bg-surface-header hover:opacity-100",
                focusRing,
              )}
            >
              <span aria-hidden="true">{s.icon}</span>
            </button>
          ))}
        </nav>
      )}
      <div
        ref={scroller}
        onScroll={onScroll}
        className="relative min-h-0 flex-1 overflow-y-auto px-2 pt-1 pb-2"
      >
        {failed ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-ink-secondary"
          >
            Emoji could not load.
            <button
              type="button"
              onClick={load}
              className={cn(
                "cursor-pointer rounded-full px-3 py-1 font-semibold text-blue hover:bg-blue-tint",
                focusRing,
              )}
            >
              Try again
            </button>
          </div>
        ) : !groups ? (
          <p role="status" className="m-0 px-4 py-10 text-center text-sm text-ink-meta">
            Loading emoji
          </p>
        ) : flat.length === 0 ? (
          <p role="status" className="m-0 px-4 py-10 text-center text-sm text-ink-meta">
            No emoji match “{query.trim()}”.
          </p>
        ) : (
          // biome-ignore lint/a11y/noStaticElementInteractions: arrow keys move between the emoji buttons inside
          <div ref={grid} onKeyDown={onGridKey} className="relative" style={{ height: layout.total }}>
            {drawn.map(({ sec, si, top, height, first, last }) => (
              <section
                key={sec.key}
                data-section={sec.key}
                aria-label={sec.label}
                className="absolute inset-x-0"
                style={{ top, height }}
              >
                <h3
                  className="sticky -top-1 z-[1] m-0 bg-linen px-1.5 pt-2 pb-1 text-[12px] font-semibold text-ink-secondary"
                  style={{ height: HEADING }}
                >
                  {sec.label}
                </h3>
                {Array.from({ length: Math.max(0, last - first + 1) }, (_, k) => {
                  const row = first + k;
                  const cells = sec.cells.slice(row * EMOJI_COLUMNS, (row + 1) * EMOJI_COLUMNS);
                  return (
                    <div
                      key={row}
                      className="absolute inset-x-0 grid grid-cols-8"
                      style={{ top: HEADING + row * rowHeight, height: rowHeight }}
                    >
                      {cells.map((c, ci) => {
                        const index = (starts[si] ?? 0) + row * EMOJI_COLUMNS + ci;
                        return (
                          <button
                            key={index}
                            type="button"
                            data-cell={index}
                            tabIndex={index === tabbable ? 0 : -1}
                            aria-label={c.label}
                            onClick={(e: MouseEvent) => pick(c.emoji, e.shiftKey)}
                            onFocus={() => {
                              setActive(index);
                              setPreview(null);
                            }}
                            onPointerEnter={() => setPreview(c)}
                            onPointerLeave={() => setPreview(null)}
                            className={cn(
                              "flex cursor-pointer items-center justify-center rounded-[10px] text-[26px] leading-none",
                              "hover:bg-surface-header focus-visible:bg-surface-header",
                              focusRing,
                            )}
                          >
                            {c.emoji}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </section>
            ))}
          </div>
        )}
      </div>
      <div className="flex h-12 flex-none items-center gap-2 border-t border-line px-3">
        <span aria-hidden="true" className="w-7 flex-none text-center text-[22px] leading-none">
          {shown?.emoji}
        </span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-ink-secondary first-letter:uppercase">
          {shown?.label ?? ""}
        </span>
        {choosingTone ? (
          <fieldset aria-label="Skin tone" className="m-0 flex items-center gap-0.5 border-0 p-0">
            {TONES.map((t, i) => (
              <button
                key={t}
                type="button"
                aria-label={TONE_NAMES[i]}
                aria-pressed={tone === i}
                onClick={() => {
                  setTone(i);
                  saveTone(i);
                  setChoosingTone(false);
                }}
                className={cn(
                  "flex h-8 w-8 cursor-pointer items-center justify-center rounded-[8px] text-[18px] leading-none",
                  tone === i ? "bg-blue-tint" : "hover:bg-surface-header",
                  focusRing,
                )}
              >
                {t}
              </button>
            ))}
          </fieldset>
        ) : (
          <button
            type="button"
            aria-label={`Skin tone: ${TONE_NAMES[tone]}`}
            onClick={() => setChoosingTone(true)}
            className={cn(
              "flex h-8 w-8 cursor-pointer items-center justify-center rounded-[8px] text-[18px] leading-none hover:bg-surface-header",
              focusRing,
            )}
          >
            {TONES[tone]}
          </button>
        )}
      </div>
    </section>
  );
}
